import { ZonedDateTime } from '@internationalized/date';

/**
 * Determines the interval in minutes based on the time range duration and container width
 */
export function getIntervalMinutes(
  durationMinutes: number,
  isLargeWidth: boolean,
): number {
  // 15 minutes or less
  if (durationMinutes <= 15) {
    return 1; // 1 minute intervals for 15 min range
  }
  // 45 minutes or less
  if (durationMinutes <= 45) {
    return 5; // 5 min intervals for both xl and lg
  }
  // 60 minutes or less
  if (durationMinutes <= 60) {
    return 5; // 5 min intervals for both xl and lg
  }
  // 4 hours or less
  if (durationMinutes <= 4 * 60) {
    return 15; // 15 minute intervals for 4 hour range
  }
  // 24 hours or less
  if (durationMinutes <= 24 * 60) {
    return isLargeWidth ? 60 : 180; // 1 hour (xl) or 3 hours (lg)
  }
  // 2 days or less
  if (durationMinutes <= 2 * 24 * 60) {
    return isLargeWidth ? 180 : 360; // 3 hours (xl) or 6 hours (lg)
  }
  // Every 3 days is 6 hours
  if (durationMinutes <= 3 * 24 * 60) {
    return 360; // 6 hour intervals for 3 day range
  }
  // 7 days or less
  if (durationMinutes <= 7 * 24 * 60) {
    return 12 * 60; // 12 hour intervals for 1 week range
  }
  // Every 15 days is 1 day
  if (durationMinutes <= 15 * 24 * 60) {
    return 24 * 60; // 1 day intervals for 15 day range
  }
  // Every 1 month (31 days) is 2 day intervals
  if (durationMinutes <= 31 * 24 * 60) {
    return 2 * 24 * 60; // 2 day intervals for 1 month range
  }
  // Every 2 months (62 days) is 7 day intervals
  if (durationMinutes <= 62 * 24 * 60) {
    return 7 * 24 * 60; // 7 day intervals for 2 month range
  }

  // More than 2 months (beyond 62 days)
  return 30.4 * 24 * 60; // 1 month (30.4 day) intervals
}

/**
 * Generates tick marks for the timeline based on the time range and interval
 */
export interface TimeTick {
  time: ZonedDateTime;
  label: string;
  position: number; // Position as percentage (0-100)
}

export function generateTimeTicks(
  startTime: ZonedDateTime,
  endTime: ZonedDateTime,
  intervalMinutes: number,
  timezone: string = 'UTC',
): TimeTick[] {
  const ticks: TimeTick[] = [];
  const durationMs = endTime.toDate().getTime() - startTime.toDate().getTime();

  // Create a reference point for tick generation
  let referenceTime: ZonedDateTime;
  const isMonthInterval = intervalMinutes >= 30.4 * 24 * 60;

  if (isMonthInterval) {
    // For month intervals, align to the first of the month
    referenceTime = startTime.set({
      day: 1,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
  } else if (intervalMinutes >= 24 * 60) {
    // For multi-day intervals, use a fixed reference date to ensure absolute consistency
    referenceTime = startTime.set({
      year: 2024,
      month: 1,
      day: 1,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
  } else {
    // For smaller intervals, use the startTime as reference at midnight
    referenceTime = startTime.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    // For intervals >= 60 minutes, align to the start of the hour
    if (intervalMinutes >= 60) {
      referenceTime = referenceTime.set({ minute: 0 });
    }
  }

  // Find the first tick time
  let currentTime = referenceTime;

  if (isMonthInterval) {
    // For month intervals, move by months instead of minutes
    while (currentTime.compare(startTime) < 0) {
      currentTime = currentTime.add({ months: 1 });
    }

    // Step back to ensure we cover the start time
    while (currentTime.subtract({ months: 1 }).compare(startTime) >= 0) {
      currentTime = currentTime.subtract({ months: 1 });
    }
  } else {
    // For non-month intervals, use minutes
    while (currentTime.compare(startTime) < 0) {
      currentTime = currentTime.add({ minutes: intervalMinutes });
    }

    // Step back to ensure we cover the start time
    while (
      currentTime.subtract({ minutes: intervalMinutes }).compare(startTime) >= 0
    ) {
      currentTime = currentTime.subtract({ minutes: intervalMinutes });
    }
  }

  // Generate ticks
  while (currentTime.compare(endTime) <= 0) {
    if (currentTime.compare(startTime) >= 0) {
      const offsetMs =
        currentTime.toDate().getTime() - startTime.toDate().getTime();
      const position = (offsetMs / durationMs) * 100;

      ticks.push({
        time: currentTime,
        label: formatTimeLabel(currentTime, intervalMinutes, timezone),
        position,
      });
    }

    // Move to next interval
    if (isMonthInterval) {
      currentTime = currentTime.add({ months: 1 });
    } else {
      currentTime = currentTime.add({ minutes: intervalMinutes });
    }
  }

  return ticks;
}

/**
 * Gets abbreviated weekday name for a ZonedDateTime
 */
function getWeekdayShort(
  date: ZonedDateTime,
  timezone: string = 'UTC',
): string {
  // Use Intl.DateTimeFormat to get the weekday in the date's timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    timeZone: timezone,
  });
  return formatter.format(date.toDate());
}

/**
 * Gets abbreviated month name for a ZonedDateTime
 */
function getMonthShort(date: ZonedDateTime, timezone: string = 'UTC'): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: timezone,
  });
  return formatter.format(date.toDate());
}

/**
 * Rounds a date to the nearest month for display purposes
 */
function roundToNearestMonth(date: ZonedDateTime): ZonedDateTime {
  const day = date.day;
  const daysInMonth = date
    .set({ day: 1 })
    .add({ months: 1 })
    .subtract({ days: 1 }).day;

  // If we're past the midpoint of the month, round to next month
  if (day > daysInMonth / 2) {
    return date.set({ day: 1 }).add({ months: 1 });
  }
  // Otherwise, round to current month
  return date.set({ day: 1 });
}

/**
 * Formats a date as HH:mm, day/date for midnight, or weekday/date for long intervals
 */
export function formatTimeLabel(
  date: ZonedDateTime,
  intervalMinutes: number = 0,
  timezone: string = 'UTC',
): string {
  // For month intervals (30.4 days), show month abbreviation
  if (intervalMinutes >= 30.4 * 24 * 60) {
    const roundedDate = roundToNearestMonth(date);
    return getMonthShort(roundedDate, timezone);
  }

  // For intervals of 1 day or more, show weekday and date
  if (intervalMinutes >= 24 * 60) {
    const weekday = getWeekdayShort(date, timezone);
    const day = date.day;
    return `${weekday} ${day}`;
  }

  // For midnight (00:00), show day and date instead of time
  if (date.hour === 0 && date.minute === 0) {
    const weekday = getWeekdayShort(date, timezone);
    const day = date.day;
    return `${weekday} ${day}`;
  }

  // Format time as HH:mm using Intl.DateTimeFormat for correct timezone display
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  });
  return formatter.format(date.toDate());
}
